/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { ActivateEntityMembership } from '../models/ActivateEntityMembership';
import type { AddClusterMembershipPayload } from '../models/AddClusterMembershipPayload';
import type { AddEntity } from '../models/AddEntity';
import type { AddEntityMembershipPayload } from '../models/AddEntityMembershipPayload';
import type { AssetAccountCreate } from '../models/AssetAccountCreate';
import type { AssetAccountRead } from '../models/AssetAccountRead';
import type { AssignTaskTemplatePayload } from '../models/AssignTaskTemplatePayload';
import type { BulkAssignTaskTemplatePayload } from '../models/BulkAssignTaskTemplatePayload';
import type { BulkCreateAssetAccounts } from '../models/BulkCreateAssetAccounts';
import type { CollectionPlanCreate } from '../models/CollectionPlanCreate';
import type { CollectionPlanRead } from '../models/CollectionPlanRead';
import type { CollectionPlanUpdate } from '../models/CollectionPlanUpdate';
import type { CommentaryDomain } from '../models/CommentaryDomain';
import type { CommentaryTypeResponse } from '../models/CommentaryTypeResponse';
import type { CompleteBankAccountTaskPayload } from '../models/CompleteBankAccountTaskPayload';
import type { CompleteTaskPayload } from '../models/CompleteTaskPayload';
import type { DataExtractionPayload } from '../models/DataExtractionPayload';
import type { DeliverableTaskReassignmentResults } from '../models/DeliverableTaskReassignmentResults';
import type { DependenciesContext } from '../models/DependenciesContext';
import type { EnrichedVisitResponse } from '../models/EnrichedVisitResponse';
import type { EntityDetailsUpdate } from '../models/EntityDetailsUpdate';
import type { EntityFileImportRequest } from '../models/EntityFileImportRequest';
import type { EntityFilter } from '../models/EntityFilter';
import type { EntityFiltersPayload } from '../models/EntityFiltersPayload';
import type { EntityRead } from '../models/EntityRead';
import type { EntitySortPayload } from '../models/EntitySortPayload';
import type { EntityWithParents } from '../models/EntityWithParents';
import type { FieldUpdate } from '../models/FieldUpdate';
import type { FinancialConnectionRead } from '../models/FinancialConnectionRead';
import type { ImportResult } from '../models/ImportResult';
import type { InviteEntityPayload } from '../models/InviteEntityPayload';
import type { MembershipRead } from '../models/MembershipRead';
import type { PaginatedSearchResponse } from '../models/PaginatedSearchResponse';
import type { RecentVisitPayload } from '../models/RecentVisitPayload';
import type { ResendInvitationPayload } from '../models/ResendInvitationPayload';
import type { TaskRead } from '../models/TaskRead';
import type { TaskSummary } from '../models/TaskSummary';
import type { TaskWithBlocks } from '../models/TaskWithBlocks';
import type { UpdateFieldValuesPayload } from '../models/UpdateFieldValuesPayload';
import type { UpdateStatementBody } from '../models/UpdateStatementBody';
import type { WorkflowCreatePayload } from '../models/WorkflowCreatePayload';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class OrchestrationService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * Assign Tasks For Deliverable
     * @param workflowId
     * @param assignmentDate
     * @returns any Successful Response
     * @throws ApiError
     */
    public assignTasksForDeliverable(
        workflowId: string,
        assignmentDate: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/orchestration/assign-tasks-for-deliverable/{workflow_id}',
            path: {
                'workflow_id': workflowId,
            },
            query: {
                'assignment_date': assignmentDate,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Update Deliverable Tasks
     * @param workflowId
     * @param requestBody
     * @param assignmentDate
     * @returns string Successful Response
     * @throws ApiError
     */
    public updateDeliverableTasks(
        workflowId: string,
        requestBody: CollectionPlanUpdate,
        assignmentDate?: (string | null),
    ): CancelablePromise<Record<string, string>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/orchestration/update-deliverable-tasks/{workflow_id}',
            path: {
                'workflow_id': workflowId,
            },
            query: {
                'assignment_date': assignmentDate,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Preview Update Deliverable Tasks
     * @param workflowId
     * @param requestBody
     * @param firstAssignmentDate
     * @returns DeliverableTaskReassignmentResults Successful Response
     * @throws ApiError
     */
    public previewUpdateDeliverableTasks(
        workflowId: string,
        requestBody: CollectionPlanUpdate,
        firstAssignmentDate?: (string | null),
    ): CancelablePromise<DeliverableTaskReassignmentResults> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/orchestration/preview-update-deliverable-tasks/{workflow_id}',
            path: {
                'workflow_id': workflowId,
            },
            query: {
                'first_assignment_date': firstAssignmentDate,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Complete Entity Task
     * @param taskId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public completeEntityTask(
        taskId: string,
        requestBody: CompleteTaskPayload,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/orchestration/complete-entity-task/{task_id}',
            path: {
                'task_id': taskId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Complete Bank Connection Task
     * @param taskId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public completeBankConnectionTask(
        taskId: string,
        requestBody: CompleteBankAccountTaskPayload,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/orchestration/complete-bank-connection-task/{task_id}',
            path: {
                'task_id': taskId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Task Version History
     * @param taskId
     * @returns TaskRead Successful Response
     * @throws ApiError
     */
    public listTaskVersionHistory(
        taskId: string,
    ): CancelablePromise<Array<TaskRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/orchestration/list-task-version-history/{task_id}',
            path: {
                'task_id': taskId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Archive Asset Account
     * @param assetAccountId
     * @returns any Successful Response
     * @throws ApiError
     */
    public archiveAssetAccount(
        assetAccountId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/orchestration/archive-asset-account/{asset_account_id}',
            path: {
                'asset_account_id': assetAccountId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Unarchive Asset Account
     * @param assetAccountId
     * @returns any Successful Response
     * @throws ApiError
     */
    public unarchiveAssetAccount(
        assetAccountId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/orchestration/unarchive-asset-account/{asset_account_id}',
            path: {
                'asset_account_id': assetAccountId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Delete Asset Account
     * @param assetAccountId
     * @returns any Successful Response
     * @throws ApiError
     */
    public deleteAssetAccount(
        assetAccountId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/orchestration/delete-asset-account/{asset_account_id}',
            path: {
                'asset_account_id': assetAccountId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Activate Entity Membership And Entity
     * @param membershipId
     * @param entityId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public activateEntityMembershipAndEntity(
        membershipId: string,
        entityId: string,
        requestBody: ActivateEntityMembership,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/orchestration/activate-membership-and-entity/{membership_id}',
            path: {
                'membership_id': membershipId,
            },
            query: {
                'entity_id': entityId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Delete Field
     * @param fieldId
     * @returns any Successful Response
     * @throws ApiError
     */
    public deleteField(
        fieldId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/orchestration/delete-field/{field_id}',
            path: {
                'field_id': fieldId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Field References
     * @param fieldId
     * @returns any Successful Response
     * @throws ApiError
     */
    public getFieldReferences(
        fieldId: string,
    ): CancelablePromise<Record<string, Array<Record<string, any>>>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/orchestration/get-field-references/{field_id}',
            path: {
                'field_id': fieldId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Scenario References
     * @param fieldScenarioId
     * @returns any Successful Response
     * @throws ApiError
     */
    public getScenarioReferences(
        fieldScenarioId: string,
    ): CancelablePromise<Record<string, Array<Record<string, any>>>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/orchestration/get-scenario-references/{field_scenario_id}',
            path: {
                'field_scenario_id': fieldScenarioId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Update Field
     * Update a field with optional dependency checking.
     *
     * Args:
     * field_id: The field to update
     * field_update: The update payload
     * ignore_dependencies: If True, skip dependency check and update directly
     *
     * Returns:
     * None on success
     *
     * Raises:
     * HTTPException 400 if field name already exists
     * HTTPException 409 with dependencies if they exist and ignore_dependencies is False
     * @param fieldId
     * @param requestBody
     * @param ignoreDependencies
     * @returns any Successful Response
     * @throws ApiError
     */
    public updateField(
        fieldId: string,
        requestBody: FieldUpdate,
        ignoreDependencies: boolean = false,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/orchestration/update-field/{field_id}',
            path: {
                'field_id': fieldId,
            },
            query: {
                'ignore_dependencies': ignoreDependencies,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Delete Formula
     * @param formulaId
     * @returns any Successful Response
     * @throws ApiError
     */
    public deleteFormula(
        formulaId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/orchestration/delete-formula/{formula_id}',
            path: {
                'formula_id': formulaId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Commentary Type
     * Creates a field for a cluster and immediately adds the associated property to the commentary feed.
     * The field will be created as global, temporal, and with a long text return type.
     * @param clusterId
     * @param namespaceId
     * @param name
     * @returns CommentaryTypeResponse Successful Response
     * @throws ApiError
     */
    public createCommentaryType(
        clusterId: string,
        namespaceId: string,
        name: string,
    ): CancelablePromise<CommentaryTypeResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/orchestration/create-commentary-type',
            query: {
                'cluster_id': clusterId,
                'namespace_id': namespaceId,
                'name': name,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Deliverable
     * @param entityId
     * @param requestBody
     * @param firstAssignmentDate
     * @returns string Successful Response
     * @throws ApiError
     */
    public createDeliverable(
        entityId: string,
        requestBody: CollectionPlanCreate,
        firstAssignmentDate?: (string | null),
    ): CancelablePromise<string> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/orchestration/create-deliverable',
            path: {
                'entity_id': entityId,
            },
            query: {
                'first_assignment_date': firstAssignmentDate,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Copy Deliverable
     * @param entityId
     * @param workflowId
     * @param clusterId
     * @returns WorkflowCreatePayload Successful Response
     * @throws ApiError
     */
    public copyDeliverable(
        entityId: string,
        workflowId: string,
        clusterId: string,
    ): CancelablePromise<WorkflowCreatePayload> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/orchestration/copy-deliverable',
            path: {
                'entity_id': entityId,
            },
            query: {
                'workflow_id': workflowId,
                'cluster_id': clusterId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Save Deliverable
     * @param entityId
     * @param collectionPlanId
     * @param requestBody
     * @returns CollectionPlanRead Successful Response
     * @throws ApiError
     */
    public saveDeliverable(
        entityId: string,
        collectionPlanId: string,
        requestBody: CollectionPlanCreate,
    ): CancelablePromise<CollectionPlanRead> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/entities/{entity_id}/orchestration/save-reporting-requirement/{collection_plan_id}',
            path: {
                'entity_id': entityId,
                'collection_plan_id': collectionPlanId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Update Entity Details
     * @param entityId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public updateEntityDetails(
        entityId: string,
        requestBody: EntityDetailsUpdate,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/entities/{entity_id}/orchestration/update-entity-details',
            path: {
                'entity_id': entityId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Clone And Complete Entity Task
     * will clone the task and response from the payload w/ all new IDs while maintaining the references between blocks
     * inside this task
     *
     * we want to use this to essentially 'resubmit' a task which we want to show up to clusters as another row
     * in their deliverables from the borrower which they can view/delete/whatever independently
     * @param entityId
     * @param taskId
     * @param requestBody
     * @returns TaskRead Successful Response
     * @throws ApiError
     */
    public cloneAndCompleteEntityTask(
        entityId: string,
        taskId: string,
        requestBody: CompleteTaskPayload,
    ): CancelablePromise<TaskRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/orchestration/clone-and-complete-entity-task/{task_id}',
            path: {
                'entity_id': entityId,
                'task_id': taskId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Draft For Edit
     * Create a draft clone of a completed task for editing. The draft can be saved
     * incrementally and resubmitted later via complete-entity-task.
     * Idempotent: returns existing draft if one already exists for this task's global_version_id.
     *
     * When a payload is provided, the draft is populated with the payload values (the user's
     * current form state) instead of reading from the original task's DB responses. This mirrors
     * the pattern of clone_and_complete_entity_task.
     * @param entityId
     * @param taskId
     * @param requestBody
     * @returns TaskWithBlocks Successful Response
     * @throws ApiError
     */
    public createDraftForEdit(
        entityId: string,
        taskId: string,
        requestBody?: (CompleteTaskPayload | null),
    ): CancelablePromise<TaskWithBlocks> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/orchestration/create-draft-for-edit/{task_id}',
            path: {
                'entity_id': entityId,
                'task_id': taskId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Complete Task From Template
     * @param entityId
     * @param taskTemplateId
     * @param requestBody
     * @returns TaskRead Successful Response
     * @throws ApiError
     */
    public completeTaskFromTemplate(
        entityId: string,
        taskTemplateId: string,
        requestBody: CompleteTaskPayload,
    ): CancelablePromise<TaskRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/orchestration/complete-task-from-template/{task_template_id}',
            path: {
                'entity_id': entityId,
                'task_template_id': taskTemplateId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Entity Task Summary
     * @param entityId
     * @returns TaskSummary Successful Response
     * @throws ApiError
     */
    public getEntityTaskSummary(
        entityId: string,
    ): CancelablePromise<Record<string, Array<TaskSummary>>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/entities/{entity_id}/orchestration/get-entity-task-summary',
            path: {
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Commentary For Entity
     * Gets all commentary entries for an entity from the commentary feed properties.
     *
     * Returns a list of CommentaryDomain objects ordered by effective date (latest first).
     * @param entityId
     * @returns CommentaryDomain Successful Response
     * @throws ApiError
     */
    public listCommentaryForEntity(
        entityId: string,
    ): CancelablePromise<Array<CommentaryDomain>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/entities/{entity_id}/orchestration/list-commentary-for-entity',
            path: {
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Update Statement
     * @param entityId
     * @param statementId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public updateStatement(
        entityId: string,
        statementId: string,
        requestBody: UpdateStatementBody,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/entities/{entity_id}/orchestration/update-statement/{statement_id}',
            path: {
                'entity_id': entityId,
                'statement_id': statementId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Save Field Values For Entity
     * @param entityId
     * @param asOfDate
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public saveFieldValuesForEntity(
        entityId: string,
        asOfDate: string,
        requestBody: UpdateFieldValuesPayload,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/orchestration/save-field-values-for-entity',
            path: {
                'entity_id': entityId,
            },
            query: {
                'as_of_date': asOfDate,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Dependency Context For Lx
     * @param entityId
     * @param lxCode
     * @returns DependenciesContext Successful Response
     * @throws ApiError
     */
    public getDependencyContextForLx(
        entityId: string,
        lxCode: string,
    ): CancelablePromise<DependenciesContext> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/entities/{entity_id}/orchestration/get-dependency-context-for-lx',
            path: {
                'entity_id': entityId,
            },
            query: {
                'lx_code': lxCode,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Publish Data Extraction Results
     * @param entityId
     * @param dataExtractionId
     * @param requestBody
     * @param markTaskComplete
     * @param publishAll
     * @returns any Successful Response
     * @throws ApiError
     */
    public publishDataExtractionResults(
        entityId: string,
        dataExtractionId: any,
        requestBody: DataExtractionPayload,
        markTaskComplete?: (boolean | null),
        publishAll: boolean = false,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/orchestration/publish-data-extraction-results-for-payload/{data_extraction_id}',
            path: {
                'entity_id': entityId,
                'data_extraction_id': dataExtractionId,
            },
            query: {
                'mark_task_complete': markTaskComplete,
                'publish_all': publishAll,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Enable Tentacle Mode Bank Connections
     * @param entityId
     * @returns any Successful Response
     * @throws ApiError
     */
    public enableTentacleModeBankConnections(
        entityId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/entities/{entity_id}/orchestration/enable-tentacle-mode-bank-connections',
            path: {
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Disable Tentacle Mode Bank Connections
     * @param entityId
     * @returns any Successful Response
     * @throws ApiError
     */
    public disableTentacleModeBankConnections(
        entityId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/entities/{entity_id}/orchestration/disable-tentacle-mode-bank-connections',
            path: {
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Disconnect Financial Connection
     * @param entityId
     * @param localConnectionId
     * @param assetAccountId
     * @param archive
     * @returns any Successful Response
     * @throws ApiError
     */
    public disconnectFinancialConnection(
        entityId: string,
        localConnectionId: string,
        assetAccountId?: (string | null),
        archive?: (boolean | null),
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/orchestration/disconnect-financial-connection',
            path: {
                'entity_id': entityId,
            },
            query: {
                'local_connection_id': localConnectionId,
                'asset_account_id': assetAccountId,
                'archive': archive,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Asset Account For Entity
     * @param entityId
     * @param requestBody
     * @returns AssetAccountRead Successful Response
     * @throws ApiError
     */
    public createAssetAccountForEntity(
        entityId: string,
        requestBody: AssetAccountCreate,
    ): CancelablePromise<AssetAccountRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/orchestration/create-asset-account',
            path: {
                'entity_id': entityId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Asset Accounts For Entity
     * Create multiple asset accounts for an entity in bulk.
     *
     * Uses the bulk service method for optimized performance when creating
     * multiple accounts at once.
     * @param entityId
     * @param requestBody
     * @returns number Successful Response
     * @throws ApiError
     */
    public createAssetAccountsForEntity(
        entityId: string,
        requestBody: BulkCreateAssetAccounts,
    ): CancelablePromise<number> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/orchestration/create-asset-accounts',
            path: {
                'entity_id': entityId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Financial Connection
     * @param entityId
     * @param remoteConnectionId
     * @param requestBody
     * @returns FinancialConnectionRead Successful Response
     * @throws ApiError
     */
    public createFinancialConnection(
        entityId: string,
        remoteConnectionId: string,
        requestBody: Record<string, any>,
    ): CancelablePromise<FinancialConnectionRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/orchestration/create-financial-connection',
            path: {
                'entity_id': entityId,
            },
            query: {
                'remote_connection_id': remoteConnectionId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Update Financial Connection
     * @param entityId
     * @param localConnectionId
     * @param remoteConnectionId
     * @param requestBody
     * @returns FinancialConnectionRead Successful Response
     * @throws ApiError
     */
    public updateFinancialConnection(
        entityId: string,
        localConnectionId: string,
        remoteConnectionId: string,
        requestBody: Record<string, any>,
    ): CancelablePromise<FinancialConnectionRead> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/entities/{entity_id}/orchestration/update-financial-connection',
            path: {
                'entity_id': entityId,
            },
            query: {
                'local_connection_id': localConnectionId,
                'remote_connection_id': remoteConnectionId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Add Entity Membership
     * @param entityId
     * @param requestBody
     * @returns MembershipRead Successful Response
     * @throws ApiError
     */
    public addEntityMembership(
        entityId: string,
        requestBody: AddEntityMembershipPayload,
    ): CancelablePromise<MembershipRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/orchestration/add-membership',
            path: {
                'entity_id': entityId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Resend Entity Invitation
     * @param entityId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public resendEntityInvitation(
        entityId: string,
        requestBody: ResendInvitationPayload,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/orchestration/resend-entity-invitation',
            path: {
                'entity_id': entityId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Invite Entity
     * @param entityId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public inviteEntity(
        entityId: string,
        requestBody: InviteEntityPayload,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/orchestration/invite-entity',
            path: {
                'entity_id': entityId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Delete Entity
     * @param entityId
     * @returns any Successful Response
     * @throws ApiError
     */
    public deleteEntity(
        entityId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/entities/{entity_id}/orchestration/delete-entity',
            path: {
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Deactivate Entity
     * @param entityId
     * @returns any Successful Response
     * @throws ApiError
     */
    public deactivateEntity(
        entityId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/entities/{entity_id}/orchestration/deactivate-entity',
            path: {
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Global Text Search Results
     * @param clusterId
     * @param search
     * @returns PaginatedSearchResponse Successful Response
     * @throws ApiError
     */
    public listGlobalTextSearchResults(
        clusterId: string,
        search: string,
    ): CancelablePromise<PaginatedSearchResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/clusters/{cluster_id}/orchestration/global-search',
            path: {
                'cluster_id': clusterId,
            },
            query: {
                'search': search,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Recent Visit Metadata
     * @param clusterId
     * @param requestBody
     * @returns EnrichedVisitResponse Successful Response
     * @throws ApiError
     */
    public listRecentVisitMetadata(
        clusterId: string,
        requestBody: RecentVisitPayload,
    ): CancelablePromise<EnrichedVisitResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/orchestration/recent-visit-metadata',
            path: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Validate Field Values For Field
     * Validate field values without saving them. Returns detailed validation results with normalized values.
     * @param clusterId
     * @param fieldId
     * @param asOfDate
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public validateFieldValuesForField(
        clusterId: string,
        fieldId: string,
        asOfDate: string,
        requestBody: UpdateFieldValuesPayload,
    ): CancelablePromise<Record<string, any>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/orchestration/validate-field-values-for-field/{field_id}',
            path: {
                'cluster_id': clusterId,
                'field_id': fieldId,
            },
            query: {
                'as_of_date': asOfDate,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Save Field Values For Field
     * @param clusterId
     * @param fieldId
     * @param asOfDate
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public saveFieldValuesForField(
        clusterId: string,
        fieldId: string,
        asOfDate: string,
        requestBody: UpdateFieldValuesPayload,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/orchestration/save-field-values-for-field/{field_id}',
            path: {
                'cluster_id': clusterId,
                'field_id': fieldId,
            },
            query: {
                'as_of_date': asOfDate,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Assign Task Template
     * @param clusterId
     * @param entityId
     * @param taskTemplateId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public assignTaskTemplate(
        clusterId: string,
        entityId: string,
        taskTemplateId: string,
        requestBody: AssignTaskTemplatePayload,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/orchestration/assign-task-template',
            path: {
                'cluster_id': clusterId,
            },
            query: {
                'entity_id': entityId,
                'task_template_id': taskTemplateId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Bulk Assign Task Template
     * Bulk assign a task template to multiple entities with specific membership assignments.
     * @param clusterId
     * @param taskTemplateId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public bulkAssignTaskTemplate(
        clusterId: string,
        taskTemplateId: string,
        requestBody: BulkAssignTaskTemplatePayload,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/orchestration/bulk-assign-task-template',
            path: {
                'cluster_id': clusterId,
            },
            query: {
                'task_template_id': taskTemplateId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Add Cluster Membership
     * @param clusterId
     * @param requestBody
     * @returns MembershipRead Successful Response
     * @throws ApiError
     */
    public addClusterMembership(
        clusterId: string,
        requestBody: AddClusterMembershipPayload,
    ): CancelablePromise<MembershipRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/orchestration/add-membership',
            path: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Entity
     * @param clusterId
     * @param namespaceId
     * @param requestBody
     * @returns EntityRead Successful Response
     * @throws ApiError
     */
    public createEntity(
        clusterId: string,
        namespaceId: string,
        requestBody: AddEntity,
    ): CancelablePromise<EntityRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/orchestration/create-entity',
            path: {
                'cluster_id': clusterId,
            },
            query: {
                'namespace_id': namespaceId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Import Entities From File
     * Bulk import entities from file data (CSV, Excel) uploaded via the UI.
     *
     * This endpoint:
     * - Receives mapped data from the frontend ImportModal
     * - Resolves parent entity names to IDs
     * - Resolves reference field names to field IDs
     * - Creates entities using the orchestration service
     *
     * Args:
     * cluster_id: Cluster ID (from auth guard)
     * namespace_id: Target namespace for all entities
     * request: Import request containing data and options
     *
     * Returns:
     * ImportResult with status, counts, and any errors
     * @param clusterId
     * @param namespaceId
     * @param requestBody
     * @returns ImportResult Successful Response
     * @throws ApiError
     */
    public importEntitiesFromFile(
        clusterId: string,
        namespaceId: string,
        requestBody: EntityFileImportRequest,
    ): CancelablePromise<ImportResult> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/orchestration/namespaces/{namespace_id}/import-entities-from-file',
            path: {
                'cluster_id': clusterId,
                'namespace_id': namespaceId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Resend Cluster Invitation
     * @param clusterId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public resendClusterInvitation(
        clusterId: string,
        requestBody: ResendInvitationPayload,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/orchestration/resend-cluster-invitation',
            path: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Filter Entities For Namespace
     * Filter entities for a namespace based on specified filters
     * @param clusterId
     * @param namespaceId
     * @param requestBody
     * @returns string Successful Response
     * @throws ApiError
     */
    public filterEntitiesForNamespace(
        clusterId: string,
        namespaceId: string,
        requestBody: EntityFiltersPayload,
    ): CancelablePromise<Array<string>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/orchestration/filter-entities-for-namespace',
            path: {
                'cluster_id': clusterId,
            },
            query: {
                'namespace_id': namespaceId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Sort Entities For Namespace
     * @param clusterId
     * @param namespaceId
     * @param requestBody
     * @returns string Successful Response
     * @throws ApiError
     */
    public sortEntitiesForNamespace(
        clusterId: string,
        namespaceId: string,
        requestBody: EntitySortPayload,
    ): CancelablePromise<Array<string>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/orchestration/sort-entities-for-namespace',
            path: {
                'cluster_id': clusterId,
            },
            query: {
                'namespace_id': namespaceId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Entities With Parents
     * @param clusterId
     * @param namespaceId
     * @param requestBody
     * @returns EntityWithParents Successful Response
     * @throws ApiError
     */
    public listEntitiesWithParents(
        clusterId: string,
        namespaceId: string,
        requestBody?: (Array<EntityFilter> | null),
    ): CancelablePromise<Array<EntityWithParents>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/orchestration/list-entities-with-parents',
            path: {
                'cluster_id': clusterId,
            },
            query: {
                'namespace_id': namespaceId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

}
